import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  BookOpenIcon,
  CreditCardIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

interface HelpCategory {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  articles: HelpArticle[];
}

interface HelpArticle {
  id: string;
  title: string;
  summary: string;
  content: string;
}

export default function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);

  // Help Categories
  const categories: HelpCategory[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpenIcon,
      articles: [
        {
          id: 'create-account',
          title: 'How do I create an account?',
          summary: 'Learn how to sign up and set up your profile',
          content: `Creating an account is easy! Follow these steps:
          
1. Click "Sign Up" in the top right corner
2. Enter your email address and create a password
3. Verify your email by clicking the link we send you
4. Complete your profile by adding a username and bio
5. You're all set! You can now start supporting creators or become a creator yourself.

If you prefer, you can also sign up using your Google or Facebook account for faster registration.`,
        },
        {
          id: 'become-creator',
          title: 'How do I become a creator?',
          summary: 'Steps to set up your creator profile',
          content: `To become a creator:

1. Log in to your account
2. Navigate to your Profile Settings
3. Click "Become a Creator"
4. Complete the creator onboarding process:
   - Add a cover image and profile photo
   - Write your creator bio
   - Create membership tiers
   - Set up payment processing
5. Submit your application for review
6. Once approved, start posting content!

Note: Creator accounts are subject to our Terms of Service and Community Guidelines.`,
        },
        {
          id: 'first-pledge',
          title: 'How do I make my first pledge?',
          summary: 'Support your favorite creators',
          content: `Supporting a creator is simple:

1. Browse creators or use search to find creators you like
2. Visit their profile page
3. Click "Become a Patron"
4. Choose a membership tier that fits your budget
5. Enter your payment information
6. Confirm your pledge

Your first charge will process immediately, and you'll gain instant access to the creator's patron-only content. Subsequent charges occur on the 1st of each month.`,
        },
      ],
    },
    {
      id: 'memberships',
      title: 'Memberships & Billing',
      icon: CreditCardIcon,
      articles: [
        {
          id: 'billing-cycle',
          title: 'When am I charged?',
          summary: 'Understanding billing cycles',
          content: `Billing works as follows:

• Initial Charge: When you first join, you're charged immediately
• Monthly Charges: All recurring charges process on the 1st of each month
• Proration: If you upgrade mid-month, you pay the prorated difference immediately
• Cancellation: If you cancel, you retain access until the end of your billing period

Example: If you join on March 15th, you're charged on March 15th, then again on April 1st, May 1st, etc.`,
        },
        {
          id: 'update-payment',
          title: 'How do I update my payment method?',
          summary: 'Managing payment information',
          content: `To update your payment method:

1. Go to Settings
2. Click "Payment Methods"
3. Add a new card or update existing details
4. Set your preferred payment method as default
5. Optionally, remove old payment methods

Your new payment method will be used for all future charges. If a payment fails, we'll automatically try your backup payment methods.`,
        },
        {
          id: 'change-tier',
          title: 'Can I change my membership tier?',
          summary: 'Upgrading or downgrading tiers',
          content: `Yes! You can change tiers at any time:

Upgrading:
• Pay the prorated difference immediately
• Gain instant access to new tier benefits
• Next full charge on your regular billing date

Downgrading:
• Change takes effect at end of current billing period
• Retain current benefits until then
• Lower charge starting next billing cycle

To change tiers, visit the creator's profile and select your new tier.`,
        },
        {
          id: 'cancel-membership',
          title: 'How do I cancel my membership?',
          summary: 'Canceling subscriptions',
          content: `To cancel a membership:

1. Go to your Memberships page
2. Find the membership you want to cancel
3. Click "Manage Membership"
4. Select "Cancel Membership"
5. Confirm your cancellation

After canceling:
• You retain access until the end of your paid period
• No further charges will occur
• You can rejoin at any time

We're sorry to see you go! If you have feedback, please let us know.`,
        },
      ],
    },
    {
      id: 'creator-tools',
      title: 'Creator Tools',
      icon: WrenchScrewdriverIcon,
      articles: [
        {
          id: 'create-post',
          title: 'How do I create a post?',
          summary: 'Publishing content for your patrons',
          content: `To create a post:

1. Go to your Creator Dashboard
2. Click "Create Post"
3. Add your content (text, images, videos, audio)
4. Set post visibility:
   - Public: Everyone can see
   - Patron-only: Only paying members
   - Tier-specific: Specific tiers only
5. Add tags and categories (optional)
6. Preview your post
7. Click "Publish" or "Schedule" for later

Pro Tips:
• Use rich text formatting
• Add multiple media files
• Pin important posts
• Schedule posts in advance`,
        },
        {
          id: 'manage-tiers',
          title: 'How do I set up membership tiers?',
          summary: 'Creating and managing tiers',
          content: `Setting up tiers:

1. Go to Creator Settings
2. Click "Membership Tiers"
3. Create tiers with:
   - Name (e.g., "Bronze", "Silver", "Gold")
   - Price per month
   - Description of benefits
   - Tier image/badge
4. Set tier limits (optional)
5. Enable or disable tiers
6. Reorder tiers by dragging

Best Practices:
• Offer clear value at each tier
• Price appropriately for your audience
• Create 3-5 tiers maximum
• Update benefits as you grow`,
        },
        {
          id: 'payouts',
          title: 'When do I get paid?',
          summary: 'Understanding creator payouts',
          content: `Creator payouts work as follows:

Payment Schedule:
• Earnings are calculated monthly
• Payouts process between the 5th-10th of each month
• First payout after account verification

Requirements:
• Minimum balance: $50
• Verified identity (KYC)
• Valid bank account or PayPal
• Tax information on file

Payout Calculation:
Gross Revenue - Platform Fee (8%) - Payment Processing (2.9% + $0.30) = Your Payout

You can view detailed earnings reports in your Creator Dashboard.`,
        },
      ],
    },
    {
      id: 'community',
      title: 'Community & Safety',
      icon: UserGroupIcon,
      articles: [
        {
          id: 'community-guidelines',
          title: 'What are the community guidelines?',
          summary: 'Rules for a safe community',
          content: `Our community guidelines:

✅ Do:
• Be respectful and kind
• Create original content
• Honor your commitments to patrons
• Report violations

❌ Don't:
• Share illegal content
• Harass or bully others
• Impersonate others
• Spam or scam
• Share adult content without age gates

Violations may result in warnings, suspensions, or permanent bans. When in doubt, contact our support team.`,
        },
        {
          id: 'report-content',
          title: 'How do I report inappropriate content?',
          summary: 'Reporting violations',
          content: `To report content:

1. Click the "..." menu on any post or comment
2. Select "Report"
3. Choose the violation type:
   - Spam
   - Harassment
   - Adult content
   - Copyright infringement
   - Other
4. Provide details
5. Submit report

All reports are reviewed by our moderation team within 24-48 hours. Reports are anonymous, and we take all concerns seriously.`,
        },
      ],
    },
    {
      id: 'security',
      title: 'Account Security',
      icon: ShieldCheckIcon,
      articles: [
        {
          id: 'enable-2fa',
          title: 'How do I enable two-factor authentication?',
          summary: 'Securing your account with 2FA',
          content: `Enable 2FA for extra security:

1. Go to Settings > Security
2. Click "Enable Two-Factor Authentication"
3. Choose your method:
   - Authenticator App (recommended)
   - SMS/Text message
4. Follow the setup instructions
5. Save your backup codes

With 2FA enabled, you'll need both your password and a verification code to log in. This protects your account even if your password is compromised.`,
        },
        {
          id: 'password-reset',
          title: 'I forgot my password. What do I do?',
          summary: 'Resetting your password',
          content: `To reset your password:

1. Click "Forgot Password?" on the login page
2. Enter your email address
3. Check your email for a reset link
4. Click the link (valid for 1 hour)
5. Create a new strong password
6. Log in with your new password

Password Tips:
• Use at least 12 characters
• Mix letters, numbers, and symbols
• Don't reuse passwords
• Use a password manager`,
        },
      ],
    },
    {
      id: 'contact',
      title: 'Contact Support',
      icon: ChatBubbleLeftRightIcon,
      articles: [
        {
          id: 'contact-support',
          title: 'How do I contact support?',
          summary: 'Getting help from our team',
          content: `We're here to help! Contact us:

Email: support@patreon-clone.com
Response Time: 24-48 hours

For urgent issues:
• Payment failures
• Account security
• Terms of Service violations

Use "Priority Support" if you're a creator with active patrons.

Before contacting:
• Check this help center
• Review our FAQ
• Check system status
• Try basic troubleshooting

Include in your message:
• Your username
• Detailed description
• Screenshots (if applicable)
• Steps you've already tried`,
        },
      ],
    },
  ];

  // Filter categories and articles based on search
  const filteredCategories = categories
    .map((category) => ({
      ...category,
      articles: category.articles.filter(
        (article) =>
          article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          article.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
          article.content.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((category) => category.articles.length > 0);

  const toggleArticle = (articleId: string) => {
    setExpandedArticle(expandedArticle === articleId ? null : articleId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <QuestionMarkCircleIcon className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-5xl font-bold mb-4">How can we help you?</h1>
          <p className="text-xl opacity-90 mb-8">
            Find answers to common questions and learn how to get the most out of our platform
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for help..."
                className="w-full pl-14 pr-4 py-4 rounded-lg text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-white shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!searchQuery ? (
          <>
            {/* Category Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() =>
                      setSelectedCategory(
                        selectedCategory === category.id ? null : category.id
                      )
                    }
                    className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow text-left ${
                      selectedCategory === category.id ? 'ring-2 ring-indigo-600' : ''
                    }`}
                  >
                    <Icon className="h-10 w-10 text-indigo-600 mb-3" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {category.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3">
                      {category.articles.length} articles
                    </p>
                    <span className="text-indigo-600 font-medium text-sm flex items-center">
                      View articles
                      <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Selected Category Articles */}
            {selectedCategory && (
              <div className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {categories.find((c) => c.id === selectedCategory)?.title}
                </h2>
                <div className="space-y-4">
                  {categories
                    .find((c) => c.id === selectedCategory)
                    ?.articles.map((article) => (
                      <div key={article.id} className="border-b last:border-b-0 pb-4">
                        <button
                          onClick={() => toggleArticle(article.id)}
                          className="w-full flex items-center justify-between text-left py-2 hover:text-indigo-600 transition-colors"
                        >
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {article.title}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {article.summary}
                            </p>
                          </div>
                          {expandedArticle === article.id ? (
                            <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0 ml-4" />
                          ) : (
                            <ChevronRightIcon className="h-6 w-6 text-gray-400 flex-shrink-0 ml-4" />
                          )}
                        </button>
                        {expandedArticle === article.id && (
                          <div className="mt-4 pl-4 text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg">
                            {article.content}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Search Results */}
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Search Results for "{searchQuery}"
            </h2>
            {filteredCategories.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-600 text-lg mb-4">
                  No results found for your search.
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {filteredCategories.map((category) => (
                  <div key={category.id} className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      {React.createElement(category.icon, {
                        className: 'h-6 w-6 mr-2 text-indigo-600',
                      })}
                      {category.title}
                    </h3>
                    <div className="space-y-4">
                      {category.articles.map((article) => (
                        <div
                          key={article.id}
                          className="border-b last:border-b-0 pb-4"
                        >
                          <button
                            onClick={() => toggleArticle(article.id)}
                            className="w-full flex items-center justify-between text-left py-2 hover:text-indigo-600 transition-colors"
                          >
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">
                                {article.title}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {article.summary}
                              </p>
                            </div>
                            {expandedArticle === article.id ? (
                              <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0 ml-4" />
                            ) : (
                              <ChevronRightIcon className="h-6 w-6 text-gray-400 flex-shrink-0 ml-4" />
                            )}
                          </button>
                          {expandedArticle === article.id && (
                            <div className="mt-4 pl-4 text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg">
                              {article.content}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Contact Support CTA */}
        <div className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">Still need help?</h2>
          <p className="text-lg opacity-90 mb-6">
            Our support team is ready to assist you
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/support"
              className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Contact Support
            </Link>
            <Link
              to="/blog"
              className="bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-800 transition-colors"
            >
              Read Our Blog
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
